const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export { API };

import { trackBeginCheckout } from "@/lib/analytics";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

// Persist UTM params on first landing so attribution survives navigation + Stripe redirect.
export function persistUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    const stored = {};
    let found = false;
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) {
        stored[k] = v;
        found = true;
      }
    });
    if (found) localStorage.setItem("cc_utm", JSON.stringify(stored));
  } catch (e) {
    /* no-op */
  }
}

function getUtm() {
  const params = new URLSearchParams(window.location.search);
  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem("cc_utm") || "{}");
  } catch (e) {
    stored = {};
  }
  const pick = (k) => params.get(k) || stored[k] || undefined;
  return {
    source: pick("utm_source"),
    medium: pick("utm_medium"),
    campaign: pick("utm_campaign"),
    content: pick("utm_content"),
    term: pick("utm_term"),
  };
}

export async function initiateCheckout(email, productSlug) {
  const res = await fetch(`${API}/checkout/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ origin_url: window.location.origin, email, product_slug: productSlug, utm: getUtm() }),
  });
  if (!res.ok) throw new Error("Failed to create checkout session");
  const data = await res.json();
  if (data.url) {
    trackBeginCheckout({ method: "stripe" });
    window.location.href = data.url;
  } else {
    throw new Error("No checkout URL received");
  }
}

export const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/files/logo_onnavy.png`;
export const LOGO_DARK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/files/logo_transparent.png`;

export async function subscribeLead(email, extra = {}) {
  const res = await fetch(`${API}/lead/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, origin_url: window.location.origin, ...extra }),
  });
  if (!res.ok) throw new Error("Failed to subscribe");
  return res.json();
}

export async function abImpression(variant, source = "exit_popup") {
  try {
    await fetch(`${API}/lead/ab-impression`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ variant, source }),
    });
  } catch (e) {
    /* no-op */
  }
}

export async function getAdminStats(key, days = 30) {
  const res = await fetch(`${API}/admin/stats?days=${days}`, {
    headers: { "X-Admin-Key": key },
  });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error("Failed to load stats");
  return res.json();
}

export async function getUtmStats(key, days = 30) {
  const res = await fetch(`${API}/admin/utm-stats?days=${days}`, {
    headers: { "X-Admin-Key": key },
  });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error("Failed to load UTM stats");
  return res.json();
}

export async function getCommunityAdminPosts(key) {
  const res = await fetch(`${API}/community/admin/posts`, {
    headers: { "X-Admin-Key": key },
  });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error("Failed to load posts");
  return res.json();
}

export async function moderateCommunity(key, body) {
  const res = await fetch(`${API}/community/admin/moderate`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Admin-Key": key },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to moderate");
  return res.json();
}

export async function featureCommunity(key, post_id, featured) {
  const res = await fetch(`${API}/community/admin/feature`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Admin-Key": key },
    body: JSON.stringify({ post_id, featured }),
  });
  if (!res.ok) throw new Error("Failed to feature");
  return res.json();
}

export async function getCommunityReports(key) {
  const res = await fetch(`${API}/community/admin/reports`, {
    headers: { "X-Admin-Key": key },
  });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error("Failed to load reports");
  return res.json();
}

export async function resolveReport(key, report_id, status) {
  const res = await fetch(`${API}/community/admin/reports/resolve`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Admin-Key": key },
    body: JSON.stringify({ report_id, status }),
  });
  if (!res.ok) throw new Error("Failed to update report");
  return res.json();
}


export async function getAdminPrompts(key) {
  const res = await fetch(`${API}/community/admin/prompts`, { headers: { "X-Admin-Key": key } });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error("Failed to load prompts");
  return res.json();
}

export async function createPrompt(key, body) {
  const res = await fetch(`${API}/community/admin/prompts`, {
    method: "POST", headers: { "Content-Type": "application/json", "X-Admin-Key": key },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to create prompt");
  return res.json();
}

export async function updatePrompt(key, id, body) {
  const res = await fetch(`${API}/community/admin/prompts/${id}`, {
    method: "PUT", headers: { "Content-Type": "application/json", "X-Admin-Key": key },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to update prompt");
  return res.json();
}

export async function deletePrompt(key, id) {
  const res = await fetch(`${API}/community/admin/prompts/${id}`, {
    method: "DELETE", headers: { "X-Admin-Key": key },
  });
  if (!res.ok) throw new Error("Failed to delete prompt");
  return res.json();
}

export async function reorderPrompts(key, ordered_ids) {
  const res = await fetch(`${API}/community/admin/prompts/reorder`, {
    method: "POST", headers: { "Content-Type": "application/json", "X-Admin-Key": key },
    body: JSON.stringify({ ordered_ids }),
  });
  if (!res.ok) throw new Error("Failed to reorder prompts");
  return res.json();
}

function currentUtm() {
  const params = new URLSearchParams(window.location.search);
  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem("cc_utm") || "{}");
  } catch (e) {
    stored = {};
  }
  const pick = (k) => params.get(k) || stored[k] || undefined;
  return {
    source: pick("utm_source"),
    medium: pick("utm_medium"),
    campaign: pick("utm_campaign"),
  };
}

export async function getPaypalConfig() {
  const res = await fetch(`${API}/paypal/config`);
  if (!res.ok) throw new Error("Failed to load PayPal config");
  return res.json();
}

export async function createPaypalOrder(email, productSlug) {
  const res = await fetch(`${API}/paypal/order`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ origin_url: window.location.origin, email, product_slug: productSlug, utm: currentUtm() }),
  });
  if (!res.ok) throw new Error("Failed to create PayPal order");
  const data = await res.json();
  return data.id;
}

export async function capturePaypalOrder(orderId) {
  const res = await fetch(`${API}/paypal/order/${orderId}/capture`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to capture PayPal order");
  return res.json();
}
